import React, { useEffect, useState } from 'react';
import { useLocationContext } from '../context/LocationContext';
import { locationConfigApi } from '../api/locationConfigApi';
import { Toast } from '../components/shared/Toast';

interface RowState {
    value: string;
    saved: string;
    loading: boolean;
}

export const LocationConfigPage: React.FC = () => {
    const { locations, loading: locationsLoading } = useLocationContext();
    const [rows, setRows] = useState<Record<string, RowState>>({});
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        if (locations.length === 0) return;
        locations.forEach(loc => {
            setRows(prev => ({ ...prev, [loc.ghl_id]: { value: '', saved: '', loading: true } }));
            locationConfigApi.get(loc.ghl_id)
                .then(cfg => setRows(prev => ({ ...prev, [loc.ghl_id]: { value: cfg.location_emr_id, saved: cfg.location_emr_id, loading: false } })))
                .catch(() => setRows(prev => ({ ...prev, [loc.ghl_id]: { value: '', saved: '', loading: false } })));
        });
    }, [locations]);

    const handleChange = (ghId: string, value: string) => {
        setRows(prev => ({ ...prev, [ghId]: { ...prev[ghId], value } }));
    };

    const handleBlur = async (ghId: string) => {
        const row = rows[ghId];
        if (!row || row.value === row.saved) return;
        try {
            const result = await locationConfigApi.upsert(ghId, row.value);
            setRows(prev => ({ ...prev, [ghId]: { ...prev[ghId], value: result.location_emr_id, saved: result.location_emr_id } }));
            setToast({ message: 'Location EMR ID saved', type: 'success' });
        } catch {
            setToast({ message: 'Failed to save Location EMR ID', type: 'error' });
        }
    };

    return (
        <div className="mapper-ui font-sans text-mapper-text max-w-[840px] mx-auto px-10 pt-[34px] pb-[60px]">
            {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

            <h1 className="text-2xl font-extrabold tracking-[-0.5px] mb-[5px]">Settings</h1>
            <p className="text-sm text-mapper-muted m-0 mb-7">Configure each Advital location and its EMR integration.</p>

            <h2 className="text-[15px] font-bold m-0 mb-3">Location EMR configuration</h2>
            <div className="bg-mapper-white border border-mapper-border rounded-[15px] overflow-hidden">
                <div className="grid grid-cols-[1.4fr_1fr_1.2fr] gap-[14px] px-[22px] py-3 border-b border-mapper-border-3 bg-mapper-surface text-[11.5px] font-bold tracking-[0.4px] text-mapper-muted-3">
                    <div>ADVITAL LOCATION</div>
                    <div>EMR SOURCE</div>
                    <div>LOCATION EMR ID</div>
                </div>

                {locationsLoading ? (
                    <div className="px-[22px] py-8 text-[13.5px] text-mapper-muted text-center">Loading…</div>
                ) : locations.length === 0 ? (
                    <div className="px-[22px] py-8 text-[13.5px] text-mapper-muted text-center">No locations found.</div>
                ) : (
                    locations.map(loc => {
                        const row = rows[loc.ghl_id];
                        return (
                            <div key={loc.ghl_id} className="grid grid-cols-[1.4fr_1fr_1.2fr] gap-[14px] items-center px-[22px] py-4 border-b border-mapper-border-4 last:border-b-0">
                                <div>
                                    <div className="text-sm font-bold">{loc.name}</div>
                                    <div className="text-[11.5px] text-mapper-muted-2 font-mono">{loc.ghl_id}</div>
                                </div>
                                <div>
                                    <span className="px-[10px] py-[3px] rounded-md bg-mapper-purple-bg text-mapper-purple text-xs font-semibold">{loc.emr}</span>
                                </div>
                                <div>
                                    <input
                                        value={row?.value ?? ''}
                                        onChange={e => handleChange(loc.ghl_id, e.target.value)}
                                        onBlur={() => handleBlur(loc.ghl_id)}
                                        disabled={row?.loading}
                                        placeholder="e.g. LOC-001"
                                        className="w-full px-[11px] py-2 border border-mapper-border-2 rounded-[9px] text-[13px] font-mono outline-none bg-mapper-surface-2 disabled:opacity-60"
                                    />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            <p className="text-[12.5px] text-mapper-muted-2 mt-3 mx-[2px]">
                The Location EMR ID is written into appointment association records when a mapping is approved, for EMRs that require it. Leave blank otherwise.
            </p>
        </div>
    );
};
